import { useFieldArray, useFormContext } from "react-hook-form";
import StopEdit from "./StopEdit";
import { MuiTooltip } from "../../../nav/nav-styles";
import { DoraButton } from "dora-shared/frontend";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronDown,
  faChevronUp,
  faPlus,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import * as React from "react";
import "./StopList.scss";
import { createEmptyStop } from "./create-empty-stop";
import { useTranslation } from "react-i18next";
import { focusOnLocationSearchInput } from "./helpers";
import { useMemo } from "react";
import { useSelector } from "../../../../redux-store";
import { selectHideLoadmeters } from "../../../../ducks/data/org-settings/selectors";
import { moveRemovedPickupLoadmeters } from "./move-removed-pickup-loadmeters";
import {
  StopUnit,
  CmrConfig,
} from "../../../../ducks/app/cargo-and-template-shared";

type StopListProps = {
  addButtonDisabled: boolean;
  isSingleStop?: boolean;
  disableAccumulatedInputs?: boolean;
  stopsLengthChanged?: () => void;
  isPickup?: boolean;
  createNew: boolean;
  // See CreateEditShipmentDialogProps.initialDataFromCache
  stopsMayReferenceStaleData?: boolean;

  // useForms inject:
  errors: any;

  // used in order to compose the name or the path of the attribute in the form, so that register can work and knows to bound values to their properties in the main object
  relativeFromPath: string;
};

const StopList = (props: StopListProps) => {
  const { t } = useTranslation(["components", "shipmentModal"]);
  const {
    isPickup,
    addButtonDisabled,
    isSingleStop,
    errors,
    relativeFromPath,
    disableAccumulatedInputs,
    createNew,
    stopsMayReferenceStaleData,
    stopsLengthChanged = () => {},
  } = props;
  const { getValues, setValue, control, watch } = useFormContext();
  const hideLoadmeters = useSelector(selectHideLoadmeters);

  const {
    fields: stops,
    append,
    remove,
    swap,
  } = useFieldArray({
    control,
    name: relativeFromPath,
  });

  const { update: updateStopUnit, remove: removeStopUnit } = useFieldArray({
    control,
    name: "stopUnits",
  });

  const { update: updateCmrConfig, remove: removeCmrConfig } = useFieldArray({
    control,
    name: "cmrConfigs",
  });

  const header = useMemo(
    () =>
      isPickup && !isSingleStop
        ? t("shipmentModal:pickupHeader")
        : isSingleStop
          ? ""
          : t("shipmentModal:dropoffHeader"),
    [t, isPickup, isSingleStop]
  );

  const addStop = () => {
    const firstPickupStopDate = getValues("pickupList[0].date");
    append({
      ...createEmptyStop(firstPickupStopDate),
      // it is clear that this is the dropoff list and there are no dropoffs, the driver created a shipment with only a pickup
      ...(!stops.length && {
        loadmeters: getValues("pickupList[0].loadmeters"),
      }),
    });
    stopsLengthChanged();
    focusOnLocationSearchInput(`${relativeFromPath}.${stops.length}`);
  };

  const moveDownClick = (i: number) => (e: React.SyntheticEvent) => {
    e.preventDefault();
    swap(i, i + 1);
  };
  const moveUpClick = (i: number) => (e: React.SyntheticEvent) => {
    e.preventDefault();
    swap(i, i - 1);
  };

  const stopUnitsWatch = watch("stopUnits") as StopUnit[];
  const cmrConfigsWatch = watch("cmrConfigs") as CmrConfig[] | undefined;

  const removeStop = (i: number) => (e: React.SyntheticEvent) => {
    e.preventDefault();
    // Read at click time: watch("pickupList") here would re-render the whole
    // dialog on every keystroke in any stop field (DISPATCHER-D5 / DORA-459).
    const pickupList = getValues("pickupList");
    const dropoffList = getValues("dropoffList");
    // console.log("maybe remove one of these?", stopUnitsWatch);
    // console.log("other watches", pickupList, dropoffList);

    // stopId from the react for loop is something entirely different from what is in the db,
    // they get new ids with use-forms, so have to find the stops by index
    if (stopUnitsWatch) {
      // A stop that isn't saved yet has no id - stop units reference it by
      // its provisoryId instead.
      if (isPickup) {
        const pickupBeingRemoved = pickupList[i];
        const pickupRef =
          pickupBeingRemoved.id || pickupBeingRemoved.provisoryId;
        // Removed in one call: removing one at a time from this snapshot
        // would shift the indices of the ones after it.
        const indexesToRemove = stopUnitsWatch.flatMap((stopUnit, i) =>
          pickupRef && stopUnit.pickupId === pickupRef ? [i] : []
        );
        if (indexesToRemove.length) {
          removeStopUnit(indexesToRemove);
        }
      } else {
        const dropoffBeingRemoved = dropoffList[i];
        const dropoffRef =
          dropoffBeingRemoved.id || dropoffBeingRemoved.provisoryId;
        for (let i = 0; i < stopUnitsWatch.length; i++) {
          const stopUnit = stopUnitsWatch[i];
          if (dropoffRef && stopUnit.dropoffId === dropoffRef) {
            updateStopUnit(i, { ...stopUnit, dropoffId: null });
          }
        }
      }
    }
    // Same pruning as stopUnits above, applied to CMR config links - a
    // config can't survive without the pickup it names (removed entirely),
    // but losing its dropoff just makes it a pickup-only config, same as a
    // stop unit does, unless another config already occupies that exact
    // pickup+no-dropoff slot (the pair must stay unique), in which case this
    // one is dropped instead of creating a duplicate.
    // Like stop units, configs reference unsaved stops by provisoryId, and
    // removals happen in one call so the snapshot's indices stay valid.
    if (cmrConfigsWatch) {
      if (isPickup) {
        const pickupBeingRemoved = pickupList[i];
        const pickupRef =
          pickupBeingRemoved.id || pickupBeingRemoved.provisoryId;
        const indexesToRemove = cmrConfigsWatch.flatMap((cmrConfig, i) =>
          pickupRef && cmrConfig.pickupId === pickupRef ? [i] : []
        );
        if (indexesToRemove.length) {
          removeCmrConfig(indexesToRemove);
        }
      } else {
        const dropoffBeingRemoved = dropoffList[i];
        const dropoffRef =
          dropoffBeingRemoved.id || dropoffBeingRemoved.provisoryId;
        const indexesToRemove: number[] = [];
        for (let i = 0; i < cmrConfigsWatch.length; i++) {
          const cmrConfig = cmrConfigsWatch[i];
          if (dropoffRef && cmrConfig.dropoffId === dropoffRef) {
            const wouldDuplicate = cmrConfigsWatch.some(
              (other, j) =>
                j !== i &&
                other.pickupId === cmrConfig.pickupId &&
                !other.dropoffId
            );
            if (wouldDuplicate) {
              indexesToRemove.push(i);
            } else {
              updateCmrConfig(i, { ...cmrConfig, dropoffId: null });
            }
          }
        }
        if (indexesToRemove.length) {
          removeCmrConfig(indexesToRemove);
        }
      }
    }
    if (isPickup && hideLoadmeters) {
      const moved = moveRemovedPickupLoadmeters(pickupList, i);
      if (moved) {
        setValue(
          `${relativeFromPath}[${moved.targetIndex}].loadmeters`,
          moved.loadmeters
        );
      }
    }
    remove(i);
    stopsLengthChanged();
  };

  return (
    <>
      {stops.map((stop, i) => (
        // Keyed by the field array's own id, not the index: swap() moves
        // react-hook-form's registered refs along with the values, so the DOM
        // must move too - otherwise checkboxes (e.g. "fix") end up with two
        // registered inputs and get read as an array of values.
        <div key={stop.id}>
          <div className="shipment-cargo-form__title">
            <div>{header}</div>
            <div className="flex">
              {!isPickup || stops.length > 1 ? (
                <MuiTooltip
                  title={t("components:buttonLabels.delete")}
                  placement="top"
                >
                  <div>
                    <DoraButton
                      onClick={removeStop(i)}
                      variant="ghost"
                      className="shipment-cargo-forms-section__title-action"
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </DoraButton>
                  </div>
                </MuiTooltip>
              ) : null}
              {createNew && stops.length > 1 && (
                <>
                  <MuiTooltip
                    title={t("components:buttonLabels.moveUp")}
                    placement="top"
                  >
                    <div>
                      <DoraButton
                        variant="ghost"
                        className="shipment-cargo-forms-section__title-action"
                        disabled={i === 0}
                        onClick={moveUpClick(i)}
                      >
                        <FontAwesomeIcon icon={faChevronUp} />
                      </DoraButton>
                    </div>
                  </MuiTooltip>
                  <MuiTooltip
                    title={t("components:buttonLabels.moveDown")}
                    placement="top"
                  >
                    <div>
                      <DoraButton
                        variant="ghost"
                        className="shipment-cargo-forms-section__title-action"
                        disabled={i === stops.length - 1}
                        onClick={moveDownClick(i)}
                      >
                        <FontAwesomeIcon icon={faChevronDown} />
                      </DoraButton>
                    </div>
                  </MuiTooltip>
                </>
              )}
            </div>
          </div>
          <StopEdit
            isPickup={isPickup}
            isSingleStop={isSingleStop}
            errors={errors}
            disableAccumulatedInputs={disableAccumulatedInputs}
            // Dot notation, not [i]: react-hook-form only recognises dot paths
            // as field-array fields, and otherwise unregisters a Controller's
            // previous name when a moved stop's index changes - wiping the
            // other stop's value at that path (e.g. its date).
            relativeFormPath={`${relativeFromPath}.${i}`}
            stopMayReferenceStaleData={stopsMayReferenceStaleData}
          />
        </div>
      ))}
      <DoraButton
        onClick={addStop}
        disabled={addButtonDisabled}
        variant="ghost"
        className="shipment-cargo-forms-section__add-button"
      >
        <FontAwesomeIcon icon={faPlus} style={{ marginRight: 8 }} />
        {t("shipmentModal:addAnother")} {header}
      </DoraButton>
    </>
  );
};

export default StopList;
